import { useState, useMemo, useRef, useEffect } from 'react';
import { FaFilter, FaTimes, FaSearch, FaSortAmountDown, FaChevronDown, FaTrash, FaCheck, FaCrown, FaStar } from 'react-icons/fa';

export default function PropertyFilter({ properties, onFilterChange }) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    builders: [],
    locations: [],
    propertyTypes: [],
    priceRange: { min: 0, max: 50 },
    featured: false,
    luxury: false,
    sortBy: 'default'
  });

  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);
  const filterPanelRef = useRef(null);
  const filterContainerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Price range settings (in AED Millions)
  const PRICE_MIN = 0;
  const PRICE_MAX = 50;
  const PRICE_STEP = 0.5;

  // Check if device is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when filter panel is open on mobile
  useEffect(() => {
    if (isFilterOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFilterOpen, isMobile]);

  // Handle click outside to close filter
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(event.target) &&
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isFilterOpen]);

  // Handle mouse enter on filter container (desktop only)
  const handleMouseEnter = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsHovered(true);
    }
  };

  // Handle mouse leave on filter container (desktop only)
  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovered(false);
    }
  };

  // Extract unique values from properties
  const filterOptions = useMemo(() => {
    const builders = [...new Set(properties.map(p => p.builder))].sort();
    const locations = [...new Set(properties.map(p => {
      return p.location.split(',')[0].trim();
    }))].sort();
    const types = [...new Set(properties.map(p => p.type))].sort();

    return { builders, locations, types };
  }, [properties]);

  // Apply filters and sorting
  const applyFilters = (newFilters) => {
    setFilters(newFilters);

    let filtered = properties.filter(property => {
      // Search filter
      if (newFilters.search) {
        const searchLower = newFilters.search.toLowerCase().trim();
        if (searchLower) {
          const matchesSearch =
            property.title?.toLowerCase().includes(searchLower) ||
            property.location?.toLowerCase().includes(searchLower) ||
            property.builder?.toLowerCase().includes(searchLower) ||
            property.type?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }
      }

      // Builder filter
      if (newFilters.builders.length > 0) {
        if (!newFilters.builders.includes(property.builder)) return false;
      }

      // Location filter
      if (newFilters.locations.length > 0) {
        const propertyLocation = property.location.split(',')[0].trim();
        if (!newFilters.locations.includes(propertyLocation)) return false;
      }

      // Property type filter
      if (newFilters.propertyTypes.length > 0) {
        if (!newFilters.propertyTypes.includes(property.type)) return false;
      }

      // Price range filter
      const priceStr = property.details?.startingPrice || '';
      const priceMatch = priceStr.match(/AED\s*([\d.]+)([KM]?)/);
      if (priceMatch) {
        let price = parseFloat(priceMatch[1]);
        const unit = priceMatch[2];
        if (unit === 'K') price *= 0.001;
        if (unit === 'M') price *= 1;

        if (price < newFilters.priceRange.min || price > newFilters.priceRange.max) {
          return false;
        }
      }

      // Featured filter
      if (newFilters.featured && !property.isFeatured) return false;

      // Luxury filter
      if (newFilters.luxury && !property.isLuxury) return false;

      return true;
    });

    // Apply sorting
    filtered = applySorting(filtered, newFilters.sortBy);

    onFilterChange(filtered);
  };

  // Sorting logic
  const applySorting = (properties, sortBy) => {
    const sorted = [...properties];

    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => {
          const priceA = extractPrice(a.details?.startingPrice);
          const priceB = extractPrice(b.details?.startingPrice);
          return priceA - priceB;
        });
      case 'price-high':
        return sorted.sort((a, b) => {
          const priceA = extractPrice(a.details?.startingPrice);
          const priceB = extractPrice(b.details?.startingPrice);
          return priceB - priceA;
        });
      case 'name-az':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'name-za':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'featured':
        return sorted.sort((a, b) => {
          if (a.isFeatured && !b.isFeatured) return -1;
          if (!a.isFeatured && b.isFeatured) return 1;
          return 0;
        });
      default:
        return sorted;
    }
  };

  // Extract price value from string
  const extractPrice = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.match(/AED\s*([\d.]+)([KM]?)/);
    if (!match) return 0;

    let price = parseFloat(match[1]);
    const unit = match[2];
    if (unit === 'K') price *= 0.001;
    if (unit === 'M') price *= 1;

    return price;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const newFilters = { ...filters, search: value };
    applyFilters(newFilters);
  };

  const handleCheckboxChange = (filterType, value) => {
    const newFilters = { ...filters };
    const currentArray = newFilters[filterType];

    if (currentArray.includes(value)) {
      newFilters[filterType] = currentArray.filter(item => item !== value);
    } else {
      newFilters[filterType] = [...currentArray, value];
    }

    applyFilters(newFilters);
  };

  const handleToggleChange = (filterType) => {
    const newFilters = { ...filters, [filterType]: !filters[filterType] };
    applyFilters(newFilters);
  };

  const handleSortChange = (e) => {
    const newFilters = { ...filters, sortBy: e.target.value };
    applyFilters(newFilters);
  };

  // Dual Range Slider Handlers
  const handleSliderMouseDown = (thumb) => (e) => {
    e.preventDefault();
    setIsDragging(thumb);
  };

  const handleSliderTouchStart = (thumb) => (e) => {
    e.stopPropagation();
    setIsDragging(thumb);
  };

  useEffect(() => {
    const handleMove = (clientX) => {
      if (!isDragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const value = (percentage / 100) * (PRICE_MAX - PRICE_MIN) + PRICE_MIN;
      const steppedValue = Math.round(value / PRICE_STEP) * PRICE_STEP;

      const newFilters = { ...filters };

      if (isDragging === 'min') {
        if (steppedValue <= filters.priceRange.max - PRICE_STEP) {
          newFilters.priceRange = { ...filters.priceRange, min: steppedValue };
          setFilters(newFilters);
          onFilterChange(applyFiltersToProperties(newFilters));
        }
      } else if (isDragging === 'max') {
        if (steppedValue >= filters.priceRange.min + PRICE_STEP) {
          newFilters.priceRange = { ...filters.priceRange, max: steppedValue };
          setFilters(newFilters);
          onFilterChange(applyFiltersToProperties(newFilters));
        }
      }
    };

    const handleMouseMove = (e) => {
      e.preventDefault();
      handleMove(e.clientX);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    };

    const handleEnd = () => {
      setIsDragging(null);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, filters]);

  // Helper function to apply filters without calling applyFilters (for slider performance)
  const applyFiltersToProperties = (newFilters) => {
    let filtered = properties.filter(property => {
      if (newFilters.search) {
        const searchLower = newFilters.search.toLowerCase().trim();
        if (searchLower) {
          const matchesSearch =
            property.title?.toLowerCase().includes(searchLower) ||
            property.location?.toLowerCase().includes(searchLower) ||
            property.builder?.toLowerCase().includes(searchLower) ||
            property.type?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }
      }

      if (newFilters.builders.length > 0) {
        if (!newFilters.builders.includes(property.builder)) return false;
      }

      if (newFilters.locations.length > 0) {
        const propertyLocation = property.location.split(',')[0].trim();
        if (!newFilters.locations.includes(propertyLocation)) return false;
      }

      if (newFilters.propertyTypes.length > 0) {
        if (!newFilters.propertyTypes.includes(property.type)) return false;
      }

      const priceStr = property.details?.startingPrice || '';
      const priceMatch = priceStr.match(/AED\s*([\d.]+)([KM]?)/);
      if (priceMatch) {
        let price = parseFloat(priceMatch[1]);
        const unit = priceMatch[2];
        if (unit === 'K') price *= 0.001;
        if (unit === 'M') price *= 1;

        if (price < newFilters.priceRange.min || price > newFilters.priceRange.max) {
          return false;
        }
      }

      if (newFilters.featured && !property.isFeatured) return false;
      if (newFilters.luxury && !property.isLuxury) return false;

      return true;
    });

    return applySorting(filtered, newFilters.sortBy);
  };

  const handlePriceInputChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    const clampedValue = Math.max(PRICE_MIN, Math.min(PRICE_MAX, numValue));

    const newFilters = { ...filters };
    if (type === 'min' && clampedValue <= filters.priceRange.max) {
      newFilters.priceRange.min = clampedValue;
    } else if (type === 'max' && clampedValue >= filters.priceRange.min) {
      newFilters.priceRange.max = clampedValue;
    }
    applyFilters(newFilters);
  };

  const clearAllFilters = () => {
    const resetFilters = {
      search: '',
      builders: [],
      locations: [],
      propertyTypes: [],
      priceRange: { min: PRICE_MIN, max: PRICE_MAX },
      featured: false,
      luxury: false,
      sortBy: 'default'
    };
    setFilters(resetFilters);
    applyFilters(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    count += filters.builders.length;
    count += filters.locations.length;
    count += filters.propertyTypes.length;
    if (filters.priceRange.min !== PRICE_MIN || filters.priceRange.max !== PRICE_MAX) count++;
    if (filters.featured) count++;
    if (filters.luxury) count++;
    return count;
  };

  const activeCount = getActiveFilterCount();
  const minPos = ((filters.priceRange.min - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;
  const maxPos = ((filters.priceRange.max - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;

  const toggleFilter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFilterOpen(prev => !prev);
  };

  const handleBackdropClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFilterOpen(false);
    setIsHovered(false);
  };

  return (
    <div className="relative w-full max-w-7xl mx-auto px-4 z-40">
      {/* Backdrop for mobile */}
      {isFilterOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={handleBackdropClick}
          onTouchEnd={handleBackdropClick}
        />
      )}

      {/* Search and Filter Bar */}
      <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 relative transition-all duration-300 hover:shadow-xl flex flex-col md:flex-row items-center gap-4 ${isFilterOpen ? 'z-50' : 'z-30'}`}>

        {/* Search Input */}
        <div className="relative w-full md:flex-1 group">
          <div className={`absolute inset-0 bg-[#c8a45e]/5 rounded-xl transition-all duration-300 pointer-events-none ${filters.search ? 'opacity-100 scale-105' : 'opacity-0 scale-100'}`} />
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#c8a45e] transition-colors duration-300" />
          <input
            type="text"
            placeholder="Search properties..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full pl-11 pr-10 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#c8a45e]/20 focus:border-[#c8a45e] focus:bg-white transition-all duration-300 shadow-sm hover:shadow-md"
          />
          {filters.search && (
            <button
              onClick={() => {
                const newFilters = { ...filters, search: '' };
                applyFilters(newFilters);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 bg-gray-100 hover:bg-red-50 rounded-full p-1.5 transition-all duration-300 transform hover:scale-110"
            >
              <FaTimes size={10} />
            </button>
          )}
        </div>

        {/* Controls Group (Sort + Filter) */}
        <div className="flex w-full md:w-auto gap-4">
          {/* Sort Select */}
          <div className="relative flex-1 md:w-48 group">
            <div className="absolute inset-0 bg-[#c8a45e]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            <FaSortAmountDown className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c8a45e] transition-transform duration-300 group-hover:scale-110 pointer-events-none z-10" />
            <select
              value={filters.sortBy}
              onChange={handleSortChange}
              className="w-full pl-11 pr-10 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-700 appearance-none cursor-pointer focus:outline-none focus:border-[#c8a45e] focus:ring-2 focus:ring-[#c8a45e]/20 hover:border-[#c8a45e]/50 transition-all duration-300 font-medium text-sm shadow-sm hover:shadow-md relative z-20 bg-transparent text-ellipsis overflow-hidden"
            >
              <option value="default">Sort: Default</option>
              <option value="featured">Featured First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name-az">Name: A-Z</option>
              <option value="name-za">Name: Z-A</option>
            </select>
            <FaChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none group-hover:text-[#c8a45e] transition-colors duration-300 z-10" />
          </div>

          {/* Filter Toggle Button */}
          <div
            className="relative group"
            ref={filterContainerRef}
          >
            <button
              className={`h-full flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl border font-bold uppercase text-xs tracking-widest transition-all duration-300 shadow-sm hover:shadow-md whitespace-nowrap ${isFilterOpen ? 'border-[#c8a45e] text-[#c8a45e] bg-[#c8a45e]/5' : 'bg-white text-gray-700 border-gray-200 hover:border-[#c8a45e]/50 hover:text-[#c8a45e]'}`}
              onClick={toggleFilter}
              type="button"
            >
              {isFilterOpen ? <FaTimes className="text-sm" /> : <FaFilter className="text-sm" />}
              <span>Filters</span>
              {activeCount > 0 && (
                <span className={`ml-1 flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold transition-colors duration-300 ${isFilterOpen ? 'bg-[#c8a45e] text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-[#c8a45e] group-hover:text-white'}`}>
                  {activeCount}
                </span>
              )}
            </button>

            {/* Expanded Filter Panel */}
            <div
              ref={filterPanelRef}
              className={`
                fixed md:absolute 
                top-1/2 md:top-[calc(100%+0.5rem)] 
                left-1/2 md:left-auto md:right-0 
                -translate-x-1/2 -translate-y-1/2 md:translate-x-0 md:translate-y-0 
                w-[90vw] md:w-[600px] 
                max-h-[85vh] md:max-h-none 
                bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden 
                transition-all duration-300 
                origin-center md:origin-top-right z-[60]
                ${isFilterOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}
              `}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-lg font-display font-bold text-gray-900">Advanced Filters</h3>
                <div className="flex items-center gap-4">
                  {activeCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-gray-500 hover:text-red-500 font-medium transition-colors underline"
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all duration-300 transform hover:rotate-90"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-8">
                {/* Quick Toggles */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Quick Filters</h4>
                  <div className="flex flex-wrap gap-4">
                    <label className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 cursor-pointer transition-all duration-300 relative overflow-hidden group ${filters.featured ? 'border-[#c8a45e] bg-[#c8a45e]/5' : 'bg-white border-gray-100 hover:border-[#c8a45e]/30 hover:bg-gray-50'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-r from-[#c8a45e]/10 to-transparent opacity-0 transition-opacity duration-300 ${filters.featured ? 'opacity-100' : 'group-hover:opacity-50'}`} />
                      <input
                        type="checkbox"
                        checked={filters.featured}
                        onChange={() => handleToggleChange('featured')}
                        className="hidden"
                      />
                      <FaStar className={`text-lg transition-colors duration-300 relative z-10 ${filters.featured ? 'text-[#c8a45e]' : 'text-gray-400 group-hover:text-[#c8a45e]'}`} />
                      <span className={`font-bold text-sm tracking-wide uppercase relative z-10 transition-colors duration-300 ${filters.featured ? 'text-[#0a0a0a]' : 'text-gray-500 group-hover:text-gray-900'}`}>Featured</span>
                    </label>

                    <label className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 cursor-pointer transition-all duration-300 relative overflow-hidden group ${filters.luxury ? 'border-[#0a0a0a] bg-[#0a0a0a] text-white' : 'bg-white border-gray-100 hover:border-gray-300 hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={filters.luxury}
                        onChange={() => handleToggleChange('luxury')}
                        className="hidden"
                      />
                      <FaCrown className={`text-lg transition-colors duration-300 ${filters.luxury ? 'text-[#c8a45e]' : 'text-gray-400 group-hover:text-[#0a0a0a]'}`} />
                      <span className={`font-bold text-sm tracking-wide uppercase transition-colors duration-300 ${filters.luxury ? 'text-white' : 'text-gray-500 group-hover:text-gray-900'}`}>Ultra Luxury</span>
                    </label>
                  </div>
                </div>

                {/* Price Range Slider */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Price Range (AED)</h4>
                    <div className="text-sm font-bold text-[#c8a45e]">
                      {filters.priceRange.min}M - {filters.priceRange.max}M+
                    </div>
                  </div>

                  <div className="relative h-20 px-4">
                    {/* Slider Track */}
                    <div className="absolute top-1/2 left-4 right-4 h-2 bg-gray-100 rounded-full shadow-inner" ref={sliderRef}>
                      <div
                        className="absolute h-full bg-gradient-to-r from-[#c8a45e] to-[#e0c07a] rounded-full opacity-90"
                        style={{
                          left: `${minPos}%`,
                          width: `${maxPos - minPos}%`
                        }}
                      />

                      {/* Thumbs */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-7 h-7 bg-white border-2 border-[#c8a45e] rounded-full shadow-[0_2px_8px_rgba(200,164,94,0.3)] cursor-grab active:cursor-grabbing hover:scale-110 active:scale-95 transition-all duration-200 z-10 flex items-center justify-center group"
                        style={{ left: `${minPos}%` }}
                        onMouseDown={handleSliderMouseDown('min')}
                        onTouchStart={handleSliderTouchStart('min')}
                      >
                        <div className="w-2 h-2 rounded-full bg-[#c8a45e] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#0a0a0a] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap pointer-events-none transform translate-y-2 group-hover:translate-y-0">
                          {filters.priceRange.min}M
                          <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] rotate-45" />
                        </div>
                      </div>

                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-7 h-7 bg-white border-2 border-[#c8a45e] rounded-full shadow-[0_2px_8px_rgba(200,164,94,0.3)] cursor-grab active:cursor-grabbing hover:scale-110 active:scale-95 transition-all duration-200 z-10 flex items-center justify-center group"
                        style={{ left: `${maxPos}%` }}
                        onMouseDown={handleSliderMouseDown('max')}
                        onTouchStart={handleSliderTouchStart('max')}
                      >
                        <div className="w-2 h-2 rounded-full bg-[#c8a45e] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#0a0a0a] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap pointer-events-none transform translate-y-2 group-hover:translate-y-0">
                          {filters.priceRange.max}M
                          <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] rotate-45" />
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-[60%] left-4 right-4 flex justify-between text-xs text-gray-400 font-medium mt-2">
                      <span>{PRICE_MIN}M</span>
                      <span>{PRICE_MAX}M+</span>
                    </div>
                  </div>
                </div>

                {/* Grid for Checkbox Lists */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Builders */}
                  {filterOptions.builders.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Developers</h4>
                      <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-2 pr-2">
                        {filterOptions.builders.map(builder => (
                          <label key={builder} className="flex items-center gap-3 cursor-pointer group py-1">
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-300 ${filters.builders.includes(builder) ? 'bg-[#c8a45e] border-[#c8a45e] shadow-sm scale-110' : 'bg-white border-gray-300 group-hover:border-[#c8a45e] group-hover:bg-[#c8a45e]/5'}`}>
                              {filters.builders.includes(builder) && <span className="text-white text-[10px] font-bold">✓</span>}
                            </div>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={filters.builders.includes(builder)}
                              onChange={() => handleCheckboxChange('builders', builder)}
                            />
                            <span className={`text-sm transition-colors duration-300 ${filters.builders.includes(builder) ? 'text-[#0a0a0a] font-bold' : 'text-gray-600 group-hover:text-[#c8a45e]'}`}>
                              {builder}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Locations */}
                  {filterOptions.locations.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Locations</h4>
                      <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-2 pr-2">
                        {filterOptions.locations.map(location => (
                          <label key={location} className="flex items-center gap-3 cursor-pointer group py-1">
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-300 ${filters.locations.includes(location) ? 'bg-[#c8a45e] border-[#c8a45e] shadow-sm scale-110' : 'bg-white border-gray-300 group-hover:border-[#c8a45e] group-hover:bg-[#c8a45e]/5'}`}>
                              {filters.locations.includes(location) && <span className="text-white text-[10px] font-bold">✓</span>}
                            </div>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={filters.locations.includes(location)}
                              onChange={() => handleCheckboxChange('locations', location)}
                            />
                            <span className={`text-sm transition-colors duration-300 ${filters.locations.includes(location) ? 'text-[#0a0a0a] font-bold' : 'text-gray-600 group-hover:text-[#c8a45e]'}`}>
                              {location}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeCount > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-gray-500 mr-2">Active:</span>
          {filters.search && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
              Search: {filters.search}
              <button
                onClick={() => {
                  const newFilters = { ...filters, search: '' };
                  applyFilters(newFilters);
                }}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          )}
          {filters.builders.map(builder => (
            <span key={builder} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
              {builder}
              <button
                onClick={() => handleCheckboxChange('builders', builder)}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          ))}
          {filters.locations.map(location => (
            <span key={location} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
              {location}
              <button
                onClick={() => handleCheckboxChange('locations', location)}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          ))}
          {filters.propertyTypes.map(type => (
            <span key={type} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
              {type}
              <button
                onClick={() => handleCheckboxChange('propertyTypes', type)}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          ))}
          {(filters.priceRange.min !== PRICE_MIN || filters.priceRange.max !== PRICE_MAX) && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
              {filters.priceRange.min}M - {filters.priceRange.max}M
              <button
                onClick={() => {
                  const newFilters = {
                    ...filters,
                    priceRange: { min: PRICE_MIN, max: PRICE_MAX }
                  };
                  applyFilters(newFilters);
                }}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          )}
          {filters.featured && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c8a45e]/10 text-[#c8a45e] text-xs font-bold border border-[#c8a45e]/20">
              Featured
              <button
                onClick={() => handleToggleChange('featured')}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          )}
          {filters.luxury && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/5 text-black text-xs font-bold border border-black/10">
              Luxury
              <button
                onClick={() => handleToggleChange('luxury')}
                className="hover:text-red-500 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </span>
          )}
          <button
            className="group flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all duration-300 ml-auto border border-transparent hover:border-red-100"
            onClick={clearAllFilters}
          >
            <FaTrash size={10} className="transition-transform duration-300 group-hover:scale-110" />
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}
